import type { AdminResponse, AdminSessionResponse } from '@manasil/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { SESSION_EXPIRED, api, tokenStore } from './api.js';

interface AuthValue {
  admin: AdminResponse | null;
  ready: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminResponse | null>(null);
  const [ready, setReady] = useState(false);

  // Silent session check on load — the same idea as the app's splash screen.
  useEffect(() => {
    if (!tokenStore.get()) {
      setReady(true);
      return;
    }
    api<AdminResponse>('/admin/auth/me')
      .then(setAdmin)
      .catch(() => tokenStore.clear())
      .finally(() => setReady(true));
  }, []);

  // Any 401 anywhere drops the session, so an expired token cannot leave a half-signed-in UI.
  useEffect(() => {
    const onExpired = () => setAdmin(null);
    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await api<AdminSessionResponse>('/admin/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    tokenStore.set(session.accessToken);
    setAdmin(session.admin);
  }, []);

  const signOut = useCallback(() => {
    tokenStore.clear();
    setAdmin(null);
  }, []);

  const value = useMemo(() => ({ admin, ready, signIn, signOut }), [admin, ready, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
